import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {Subscription, filter} from 'rxjs';
import {User} from '../../model/user';
import {Version} from '../../model/version';
import {NavigationStateService} from './navigation-state.service';

// Préfixes de route couverts par chaque groupe de menu, pour ouvrir
// automatiquement le bon groupe à l'arrivée sur une page (et à chaque
// navigation) sans action de l'utilisateur. /booking/calendar est utilisé à
// la fois par "Mon SDIS" (lecture, ?ecawin=true) et "Matériel" (gestion,
// ?events=true) : ce cas est désambiguïsé explicitement dans
// openGroupForUrl avant d'utiliser cette table par préfixe.
const GROUP_ROUTE_PREFIXES: Record<string, string[]> = {
  administration: ['/users', '/tags', '/permissions', '/sqrs-dashboard', '/annonce/admin', '/convocations/ecawin', '/formulaires/rapports'],
  sdis: ['/annonce', '/annuaire', '/msgr', '/statistics'],
  roulages: ['/roulage'],
  materiel: ['/booking'],
  profil: ['/convocations', '/formulaires', '/carte-legitimation'],
  apr: ['/apr'],
};

// Même principe que GROUP_ROUTE_PREFIXES, un niveau plus bas : ouvre la
// sous-section de 3e niveau concernée ("Interventions", "Utilisateurs") à
// l'arrivée sur une de ses pages, puisqu'elle reste repliée par défaut sinon.
const SUBGROUP_ROUTE_PREFIXES: Record<string, string[]> = {
  interventions: ['/statistics'],
  utilisateurs: ['/users', '/tags', '/permissions'],
};

@Component({
    selector: 'navigation',
    templateUrl: 'navigation.template.html',
    standalone: false
})
export class NavigationComponent implements OnInit, OnDestroy {
  @Input()
  user: User;

  @Input()
  version: Version;

  private routerSubscription?: Subscription;

  constructor(
    private router: Router,
    public navState: NavigationStateService
  ) {}

  ngOnInit(): void {
    this.openGroupForUrl(this.router.url);
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => this.openGroupForUrl(event.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }

  private openGroupForUrl(url: string): void {
    // /booking/calendar est partagé par "Mon SDIS" (?ecawin=true) et
    // "Matériel" (?events=true) : un simple préfixe de chemin ne peut pas
    // les distinguer, d'où ce cas particulier avant la table générique.
    if (url.startsWith('/booking/calendar') && url.includes('ecawin=true')) {
      this.navState.openGroup.set('sdis');
      return;
    }
    const groupId = Object.keys(GROUP_ROUTE_PREFIXES).find(id =>
      GROUP_ROUTE_PREFIXES[id].some(prefix => url.startsWith(prefix))
    );
    if (groupId) {
      this.navState.openGroup.set(groupId);
    }

    const subgroupId = Object.keys(SUBGROUP_ROUTE_PREFIXES).find(id =>
      SUBGROUP_ROUTE_PREFIXES[id].some(prefix => url.startsWith(prefix))
    );
    if (subgroupId) {
      this.navState.openSubgroup.set(subgroupId);
    }
  }

  getLogo() {
    return 'assets/images/' + this.version.sdis.code + '/logo.png';
  }
}
